import { useMemo, useRef } from 'react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { StateScenarioCategoryStat } from '@/hooks/useStateScenarioStats';

interface ScenarioCategoryHeatmapProps {
  categoryStats: StateScenarioCategoryStat[];
  categories: string[];
  selectedState: string | null;
  onStateClick: (state: string) => void;
}

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

/** Short category labels for column headers. Compound "X / Y" categories are
    disambiguated so they don't collide into duplicate columns — the old
    `cat.split(' ')[0]` fallback turned both "Hepatology / …" into "Hepatology"
    and the four Neuro categories into indistinguishable "Neuro" columns. Full
    names still show on hover via the column's title attribute. */
function shortCategoryLabel(cat: string): string {
  const map: Record<string, string> = {
    'Cancer & Oncology': 'Cancer',
    'Heart & Cardiovascular': 'Heart',
    'Cardiac Surgery': 'Cardiac Sx',
    'Emergency & Critical Care': 'ER',
    'Transplant': 'Transplant',
    "Women's Health & OB/GYN": "Women's",
    'Digestive & GI': 'GI',
    'Gastroenterology': 'Gastro',
    'Orthopedic & Joint': 'Ortho',
    'Orthopedic Surgery': 'Ortho Sx',
    'General Surgery': 'Gen Surgery',
    'Colorectal Surgery': 'Colorectal',
    'Endocrine Surgery': 'Endocrine Sx',
    'Head & Neck Surgery': 'Head & Neck',
    'Oral Surgery': 'Oral Sx',
    'Plastic Surgery': 'Plastic',
    'Thoracic Surgery': 'Thoracic',
    'Vascular Surgery': 'Vascular',
    'Pediatric': 'Peds',
    'Pediatric Urology': 'Peds Uro',
    'Neonatal / Pediatric Surgery': 'Neonatal',
    'Spine & Back Pain': 'Spine',
    'Diabetes & Endocrinology': 'Diabetes',
    'Neurological': 'Neuro',
    'Neurology / Behavioral Health': 'Neuro-Beh',
    'Neurology / Rehabilitation': 'Neuro-Rehab',
    'Neurotology / Head & Neck Surgery': 'Neuro-ENT',
    'Hepatology / Critical Care': 'Hep-CC',
    'Hepatology / Transplant Surgery': 'Hep-Tx',
    'Preventive & Screening': 'Prevent',
    'Prenatal Care': 'Prenatal',
    'Interventional Radiology': 'Intervent Rad',
    'Otolaryngology': 'ENT',
    'Sleep Medicine': 'Sleep',
    'Behavioral Health': 'Behavioral',
    'Urology': 'Urology',
    'Mental Health': 'Mental',
    'Pulmonology': 'Pulm',
    'Infectious Disease': 'Infect',
    'Other Medical Scenarios': 'Other',
  };
  return map[cat] || cat.split(' ')[0];
}

/** Minimum scenarios behind a state×category cell for its median to be trusted.
    Below this, a small handful of scenarios can swing the median to absurd
    values (e.g. a single scenario producing a +1,500% cell), so we render the
    cell as low-confidence rather than presenting the noise as a real figure. */
const MIN_CONFIDENT_SCENARIOS = 5;

function getCellColor(markup: number, isAhp: boolean): string {
  // Clamp to -60..+60 range for color mapping
  const t = Math.max(-1, Math.min(1, markup / 60));
  if (isAhp) {
    if (t <= 0) {
      const a = Math.abs(t);
      return `rgba(0,40,104,${0.1 + a * 0.5})`;
    } else {
      return `rgba(178,34,52,${0.1 + t * 0.5})`;
    }
  } else {
    if (t <= 0) {
      const a = Math.abs(t);
      return `rgba(34,197,94,${0.1 + a * 0.5})`;
    } else {
      return `rgba(239,68,68,${0.1 + t * 0.5})`;
    }
  }
}

export function ScenarioCategoryHeatmap({
  categoryStats,
  categories,
  selectedState,
  onStateClick,
}: ScenarioCategoryHeatmapProps) {
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';
  const scrollRef = useRef<HTMLDivElement>(null);

  const textColor = isAhp ? (isDark ? '#FAF9F6' : '#1B1B1B') : '#fff';
  const mutedText = isAhp ? (isDark ? 'rgba(250,249,246,0.5)' : 'rgba(0,0,0,0.4)') : 'rgba(255,255,255,0.4)';
  const headerBg = isAhp ? (isDark ? 'rgba(0,20,60,0.8)' : 'rgba(0,40,104,0.08)') : 'rgba(0,0,0,0.6)';
  const borderColor = isAhp ? (isDark ? 'rgba(197,165,90,0.15)' : 'rgba(0,0,0,0.08)') : 'rgba(255,255,255,0.08)';

  // Build lookup: state → category → stats
  const { lookup, states } = useMemo(() => {
    const lk: Record<string, Record<string, StateScenarioCategoryStat>> = {};
    // Compute overall median per state for sorting
    const stateMedians: Record<string, number[]> = {};

    for (const row of categoryStats) {
      if (!lk[row.state]) lk[row.state] = {};
      lk[row.state][row.category] = row;
      // Only well-sampled cells feed the state's sort rank — otherwise a single
      // noisy small-sample category could push a state to the top.
      if (Number(row.scenario_count) >= MIN_CONFIDENT_SCENARIOS) {
        if (!stateMedians[row.state]) stateMedians[row.state] = [];
        stateMedians[row.state].push(Number(row.median_markup_pct));
      }
    }

    // Sort states by average of category medians (worst first)
    const sorted = Object.keys(lk).sort((a, b) => {
      const avgA = stateMedians[a] ? stateMedians[a].reduce((s, v) => s + v, 0) / stateMedians[a].length : 0;
      const avgB = stateMedians[b] ? stateMedians[b].reduce((s, v) => s + v, 0) / stateMedians[b].length : 0;
      return avgB - avgA;
    });

    return { lookup: lk, states: sorted };
  }, [categoryStats]);

  if (categories.length === 0 || states.length === 0) {
    return <div className="text-center py-8" style={{ color: mutedText }}>No scenario data available</div>;
  }

  return (
    <div className="space-y-2">
      <div className="text-xs flex items-center gap-4" style={{ color: mutedText }}>
        <span>Rows sorted by overall markup (worst first). Values show median markup % vs national average.</span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: isAhp ? 'rgba(0,40,104,0.5)' : 'rgba(34,197,94,0.5)' }} />
          Below avg
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: isAhp ? 'rgba(178,34,52,0.5)' : 'rgba(239,68,68,0.5)' }} />
          Above avg
        </span>
        <span className="opacity-70">* fewer than {MIN_CONFIDENT_SCENARIOS} scenarios — low confidence</span>
      </div>

      <div ref={scrollRef} className="overflow-x-auto rounded-xl" style={{ border: `1px solid ${borderColor}` }}>
        <table className="w-full text-xs" style={{ color: textColor, minWidth: `${200 + categories.length * 72}px` }}>
          <thead>
            <tr style={{ backgroundColor: headerBg }}>
              <th
                className="text-left px-3 py-2 sticky left-0 z-10"
                style={{ backgroundColor: headerBg, minWidth: '80px' }}
              >
                State
              </th>
              {categories.map((cat) => (
                <th
                  key={cat}
                  className="text-center px-1 py-2 font-medium"
                  style={{ minWidth: '64px' }}
                  title={cat}
                >
                  <div className="truncate">{shortCategoryLabel(cat)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {states.map((state) => {
              const isSelected = state === selectedState;
              return (
                <tr
                  key={state}
                  className="cursor-pointer transition-colors hover:opacity-90"
                  style={{
                    borderLeft: isSelected ? `3px solid ${isAhp ? '#C5A55A' : '#00ff41'}` : '3px solid transparent',
                  }}
                  onClick={() => onStateClick(state === selectedState ? state : state)}
                >
                  <td
                    className="px-3 py-1.5 font-medium sticky left-0 z-10 whitespace-nowrap"
                    style={{
                      backgroundColor: isSelected
                        ? (isAhp ? (isDark ? 'rgba(197,165,90,0.2)' : 'rgba(197,165,90,0.1)') : 'rgba(0,255,65,0.15)')
                        : (isAhp ? (isDark ? '#0a1628' : '#FAF9F6') : '#111'),
                    }}
                  >
                    <span className="font-bold">{state}</span>
                    <span className="ml-1.5 opacity-50 hidden md:inline">{STATE_NAMES[state]}</span>
                  </td>
                  {categories.map((cat) => {
                    const cell = lookup[state]?.[cat];
                    if (!cell) {
                      return (
                        <td key={cat} className="px-1 py-1.5 text-center" style={{ backgroundColor: 'rgba(128,128,128,0.08)' }}>
                          <span style={{ color: mutedText }}>—</span>
                        </td>
                      );
                    }
                    const markup = Number(cell.median_markup_pct);
                    const lowConfidence = Number(cell.scenario_count) < MIN_CONFIDENT_SCENARIOS;
                    // Low-sample cells: show the value but muted with no alarming
                    // color fill, so real-but-noisy figures don't read as headline
                    // markups. Full context is in the tooltip.
                    if (lowConfidence) {
                      return (
                        <td
                          key={cat}
                          className="px-1 py-1.5 text-center tabular-nums"
                          style={{ backgroundColor: 'rgba(128,128,128,0.06)', color: mutedText }}
                          title={`${state} × ${cat}: ${markup >= 0 ? '+' : ''}${markup.toFixed(1)}% — only ${cell.scenario_count} scenario${cell.scenario_count === 1 ? '' : 's'} / ${cell.hospital_count} hospital${cell.hospital_count === 1 ? '' : 's'} (low confidence)`}
                        >
                          <span style={{ opacity: 0.7 }}>{markup >= 0 ? '+' : ''}{markup.toFixed(0)}%</span>
                          <span style={{ fontSize: '8px', opacity: 0.6 }}>*</span>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={cat}
                        className="px-1 py-1.5 text-center tabular-nums font-medium"
                        style={{ backgroundColor: getCellColor(markup, isAhp) }}
                        title={`${state} × ${cat}: ${markup >= 0 ? '+' : ''}${markup.toFixed(1)}% (${cell.scenario_count} scenarios / ${cell.hospital_count} hospitals)`}
                      >
                        {markup >= 0 ? '+' : ''}{markup.toFixed(0)}%
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
